import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormattedMessage, injectIntl, intlFormatPropTypes } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@ps-design-system/components/Button";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog,
  DialogContent,
} from "@ps-design-system/components/Dialog";
import { Form } from "@ps-design-system/components/Form";
import { InputField } from "@ps-design-system/components/Input";
import { Text } from "@ps-design-system/components/Text";

import { updateProfile } from "../../app-main/actions/profile";
import { selectAuthAccessToken } from "../../app-main/selectors/auth.selectors";
import {
  selectExtUserId,
  selectFirstName,
  selectLastName,
} from "../../app-main/selectors/profile.selectors";

import {
  ProfileNameFormValues,
  profileNameSchema,
} from "./EditNameModal.schema";

const EDIT_NAME_FORM_ID = "edit-name-form";

type EditNameModalProps = {
  intl: intlFormatPropTypes;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const EditNameModal = ({ intl, open, onOpenChange }: EditNameModalProps) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAuthAccessToken);
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const isUserFAEnabled = useSelector(selectExtUserId);
  const shouldUseNewEndpoints = Boolean(isUserFAEnabled);

  const schema = profileNameSchema(intl);

  const form = useForm<ProfileNameFormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: { firstName: firstName ?? "", lastName: lastName ?? "" },
    reValidateMode: "onChange",
  });

  const handleOnClose = () => {
    // reset the form before the user leaves
    form.reset();
    onOpenChange(false);
  };

  const handleSubmit = async ({
    firstName,
    lastName,
  }: {
    firstName: string;
    lastName: string;
  }) => {
    form.clearErrors();

    const payload = shouldUseNewEndpoints
      ? {
          name: { first_name: firstName, last_name: lastName },
        }
      : {
          first_name: firstName,
          last_name: lastName,
        };
    dispatch(updateProfile(accessToken, payload))
      .then(() => {
        handleOnClose();
      })
      .catch(() =>
        form.setError("root.serverError", {
          type: "400",
          message: intl.formatMessage({
            id: "hosting.profile_page.save_error",
            defaultMessage:
              "Something went wrong, please try again. If the problem persists please refresh.",
          }),
        }),
      );
  };

  return (
    <Dialog onOpenChange={handleOnClose} open={open}>
      <DialogContent size="md" testId="edit-name-modal">
        <DialogHeader showCloseButton={true}>
          <DialogTitle className="tw-my-0">
            <FormattedMessage
              id="hosting.profile_page.name_label"
              defaultMessage="Name"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="hosting.profile_page.name_description"
              defaultMessage="How your name appears to guests and hosts on Peerspace."
            />
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="tw-pb-16">
          <Form
            {...form}
            fullWidth={true}
            id={EDIT_NAME_FORM_ID}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputField
              fullWidth={true}
              label={
                <FormattedMessage
                  id="hosting.profile_page.first_name_label"
                  defaultMessage="First Name"
                />
              }
              name="firstName"
              required={true}
            />
            <InputField
              fullWidth={true}
              label={
                <FormattedMessage
                  id="hosting.profile_page.last_name_label"
                  defaultMessage="Last Name"
                />
              }
              name="lastName"
              required={true}
            />
            {form.formState?.errors?.root?.serverError && (
              <Text className="tw-text-red-800" element="p" typeface="body-1">
                {form.formState.errors.root.serverError.message}
              </Text>
            )}
          </Form>
        </DialogBody>

        <DialogFooter className="sm:tw-justify-between">
          <Button
            appearance="black"
            onClick={handleOnClose}
            variant="underline-m"
            className="xs:tw-hidden sm:tw-flex"
          >
            <FormattedMessage
              id="hosting.profile_page.cancel_button"
              defaultMessage="Cancel"
            />
          </Button>
          <Button
            appearance="black"
            disabled={form.formState.isSubmitting}
            form={EDIT_NAME_FORM_ID}
            showSpinner={form.formState.isSubmitting}
            type="submit"
          >
            <FormattedMessage
              id="hosting.profile_page.save_button"
              defaultMessage="Save"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default injectIntl(EditNameModal);
